import { Component, OnInit, ViewChild, ElementRef, inject, DestroyRef, OnDestroy } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef, MatDialog } from "@angular/material/dialog";
import {
  AppSettingsService,
  OidcAuthService,
  SpinnerService,
  StudyCodeHelperService,
} from "../../../core";
import { catchError, from, map, mergeMap, Subject, takeUntil, tap, throwError } from "rxjs";
import { BucketItemService } from "../service/bucket-item.service";
import { IBreadcrumbItem } from "../../../main/layout/tool-bar/model/breadcrumb.model";
import { MessageService } from "../../../main";
import { HttpErrorResponse } from "@angular/common/http";
import { ICommitRequest } from "../models/commit-request.model";
import { ICreateFolderRequest } from "../models/create-folder-request.model";
import { BreadcrumbDataService } from "../../../main/layout/tool-bar/breadcrumb-service/breadcrumb-data.service";
import { ChecksumCalculatorService } from "../../../extensions/checksum-calculator";
import {
  CommonConsants,
  ErrorConstants,
  BucketTypeConstants,
} from "../constants/component-constants";
import { DuplicateFileUploadDialogComponent } from "./duplicate-file-upload-dialog/duplicate-file-upload-dialog.component";
import { JustificationDialogComponent } from "../../../shared/justification-dialog/justification-dialog.component";
import { UtilityService } from '../../../core/services/utility.service';
import { MatButton } from "@angular/material/button";
import { MatProgressSpinner } from "@angular/material/progress-spinner";

interface SelectionDisplayItem {
  key: string;
  path: string;
  name: string;
  type: "file" | "folder";
  size: number;
  sizeLabel: string;
  depth: number;
}

interface SelectionTreeNode {
  key: string;
  path: string;
  name: string;
  type: "file" | "folder";
  size: number;
  children: Map<string, SelectionTreeNode>;
}

@Component({
    selector: "app-upload-drag-and-drop",
    templateUrl: "./upload-drag-and-drop.component.html",
    styleUrls: ["./upload-drag-and-drop.component.scss"],
    imports: [MatButton, MatProgressSpinner]
})
export class UploadDragAndDropComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<UploadDragAndDropComponent>>(MatDialogRef);
  private bucketItemsService = inject(BucketItemService);
  private breadcrumbDataService = inject(BreadcrumbDataService);
  private messageService = inject(MessageService);
  private appSettingService = inject(AppSettingsService);
  private spinnerService = inject(SpinnerService);
  private checksumService = inject(ChecksumCalculatorService);
  dialog = inject(MatDialog);
  private utilityService = inject(UtilityService);
  private studyCodeHelperService = inject(StudyCodeHelperService);
  private oidcAuthService = inject(OidcAuthService);
  private readonly beforeUnloadHandler = (): void => this.sendBeaconUnlock();

  @ViewChild("fileInput") fileInput: ElementRef<HTMLInputElement>;
  isDragOver = false;
  inputElement: HTMLInputElement = null;
  public isLoading = false;
  public completedRequestedCount: boolean[] = [];
  private readonly cancel$ = new Subject<void>();
  private readonly destroyRef = inject(DestroyRef);
  public commitRequest: ICommitRequest;
  public currentBreadcrumbItem: IBreadcrumbItem = null;
  public guid: string = "";
  private hasValidationErrorsInUploadResponse = false;
  private validationErrorToasterShown = false;
  private readonly maxUploadFileSizeBytes = 2147483648;
  private readonly maxUploadFileCount = 100000;
  
  // Store pending files and folders to process on OK click
  public pendingFiles: File[] = [];
  public pendingEmptyFolders: string[] = [];
  private pendingPackageFolderName: string = null;
  private hasTopLevelFiles: boolean = false;

  // Frozen once the package-folder creation flow starts in onOkClick, so isPackageLevel()
  // doesn't flip after currentBreadcrumbItem is internally updated with the newly created
  // package folder's PackageId/DirectoryName
  private packageLevelSnapshot: boolean | null = null;
  private packageFolderCreated = false;

  // Cache of getDisplayItems(), keyed by the pendingFiles/pendingEmptyFolders references it was built from
  private displayItemsCache: SelectionDisplayItem[] | null = null;
  private displayItemsCacheFiles: File[] | null = null;
  private displayItemsCacheFolders: string[] | null = null;

  ngOnInit(): void {
    this.breadcrumbDataService.selectedBreadcrumb$
      .pipe(
        tap((item) => {
          this.currentBreadcrumbItem = item as IBreadcrumbItem;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.spinnerService.getMessage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(show => { this.isLoading = show; });

    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    if (this.guid && this.currentBreadcrumbItem?.PackageId) {
      this.bucketItemsService.UnlockPackage(this.currentBreadcrumbItem.PackageId, this.guid).subscribe({
        next: () => {
          this.guid = '';
        },
        error: () => {}
      });
    }
  }

  private sendBeaconUnlock(): void {
    if (!this.guid || !this.currentBreadcrumbItem?.PackageId) {
      return;
    }
    const url = `${this.appSettingService.baseApiUrl}/api/BucketItem/unlock-package`;
    const payload = JSON.stringify({ packageId: this.currentBreadcrumbItem.PackageId, locktransactionId: this.guid });
    const authHeader = this.oidcAuthService.authorizationHeaderValue;
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': authHeader },
      body: payload,
      keepalive: true
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isLoading) {
      this.isDragOver = false;
      return;
    }
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.isLoading) {
      return;
    }

    const items = event.dataTransfer?.items;
    if (items && items.length > 0) {
      this.processDroppedItems(items);
    }
  }

  onFileInputClick(): void {
    if (this.isLoading) {
      return;
    }

    const isPackageLevel = this.currentBreadcrumbItem.DirectoryName.split("/").length === 2;

    // At package level, enable folder selection
    if (isPackageLevel) {
      if (this.isDirectoryPickerSupported()) {
        this.selectFolderViaDirectoryPicker();
        return;
      }
      this.fileInput.nativeElement.setAttribute('webkitdirectory', '');
      this.fileInput.nativeElement.removeAttribute('multiple');
    } else {
      // At subfolder level, allow file and folder selection
      this.fileInput.nativeElement.removeAttribute('webkitdirectory');
      this.fileInput.nativeElement.setAttribute('multiple', '');
    }
    
    this.fileInput.nativeElement.click();
  }

  onFolderInputClick(): void {
    if (this.isLoading) {
      return;
    }

    if (this.isDirectoryPickerSupported()) {
      this.selectFolderViaDirectoryPicker();
      return;
    }
    this.fileInput.nativeElement.setAttribute('webkitdirectory', '');
    this.fileInput.nativeElement.removeAttribute('multiple');
    this.fileInput.nativeElement.click();
  }

  // File System Access API supports recursing into empty subfolders, unlike input[webkitdirectory]'s FileList
  private isDirectoryPickerSupported(): boolean {
    return typeof (window as any).showDirectoryPicker === 'function';
  }

  private async selectFolderViaDirectoryPicker(): Promise<void> {
    let dirHandle: any;
    try {
      dirHandle = await (window as any).showDirectoryPicker();
    } catch (error) {
      if ((error as any)?.name === 'AbortError') {
        return;
      }
      // Directory picker failed unexpectedly; fall back to the legacy folder input.
      this.fileInput.nativeElement.setAttribute('webkitdirectory', '');
      this.fileInput.nativeElement.removeAttribute('multiple');
      this.fileInput.nativeElement.click();
      return;
    }

    const files: File[] = [];
    const emptyFolders: string[] = [];
    await this.traverseDirectoryHandle(dirHandle, '', files, emptyFolders);

    if (this.exceedsFileCountLimit(files)) {
      this.rejectExcessiveFileCount();
      return;
    }

    if (files.length === 0) {
      this.clearPendingSelection();
      this.messageService.alert(
        ErrorConstants.EMPTY_FOLDERS_DETECTED_UPLOAD_FOLDER,
        this.appSettingService.toastrDisplayTime
      );
      return;
    }

    this.pendingFiles = files;
    this.pendingEmptyFolders = [];
    this.pendingPackageFolderName = dirHandle.name;
    this.hasTopLevelFiles = false;

    if (emptyFolders.length > 0) {
      this.messageService.alert(
        ErrorConstants.EMPTY_FOLDERS_DETECTED_UPLOAD_FOLDER,
        this.appSettingService.toastrDisplayTime
      );
    }
  }

  private async traverseDirectoryHandle(
    dirHandle: any,
    path: string,
    files: File[],
    emptyFolders: string[]
  ): Promise<void> {
    const entries: any[] = [];
    for await (const handle of dirHandle.values()) {
      entries.push(handle);
    }

    if (entries.length === 0) {
      emptyFolders.push(path + dirHandle.name);
      return;
    }

    const subPath = path + dirHandle.name + '/';
    for (const handle of entries) {
      if (handle.kind === 'file') {
        const file: File = await handle.getFile();
        Object.defineProperty(file, 'webkitRelativePath', {
          value: subPath + file.name,
          writable: true,
          configurable: true
        });
        files.push(file);
      } else if (handle.kind === 'directory') {
        await this.traverseDirectoryHandle(handle, subPath, files, emptyFolders);
      }
    }
  }

  onFileSelected(event: Event): void {
    if (this.isLoading) {
      return;
    }

    const input = event.target as HTMLInputElement;
    if (!input.files) {
      return;
    }

    if (input.files.length === 0) {
      // A plain (non-directory) file picker at subfolder level reports 0 files when the user just cancels; only warn for folder pickers.
      if (input.hasAttribute('webkitdirectory')) {
        this.pendingFiles = [];
        this.pendingEmptyFolders = [];
        this.pendingPackageFolderName = null;
        this.hasTopLevelFiles = false;
        this.messageService.alert(
          ErrorConstants.EMPTY_FOLDERS_DETECTED_UPLOAD_FOLDER,
          this.appSettingService.toastrDisplayTime
        );
      }
      return;
    }

    this.inputElement = input;
    const validFiles = Array.from(input.files);
    if (this.exceedsFileCountLimit(validFiles)) {
      this.rejectExcessiveFileCount();
      return;
    }

    if (this.isPackageLevel() && this.hasMultipleTopLevelFolders(validFiles)) {
      this.rejectMultipleFoldersSelection();
      return;
    }

    this.pendingFiles = validFiles;
    this.pendingEmptyFolders = [];
    this.pendingPackageFolderName = null;
    this.hasTopLevelFiles = false;
    
    // Extract the top-level folder name from the first file's path
    if (validFiles.length > 0 && validFiles[0].webkitRelativePath) {
      const pathParts = validFiles[0].webkitRelativePath.split('/');
      if (pathParts.length > 1) {
        this.pendingPackageFolderName = pathParts[0];
      }
    }
  }

  private async processDroppedItems(items: DataTransferItemList): Promise<void> {
    this.pendingFiles = [];
    this.pendingEmptyFolders = [];
    this.hasTopLevelFiles = false;
    this.pendingPackageFolderName = null;
    let topLevelFolderCount = 0;

    const traversePromises: Promise<void>[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.kind !== 'file') {
        continue;
      }

      const entry = item.webkitGetAsEntry();

      if (!entry) {
        continue;
      }

      if (entry.isFile) {
        this.hasTopLevelFiles = true;
      } else if (entry.isDirectory && !this.pendingPackageFolderName) {
        this.pendingPackageFolderName = entry.name;
        topLevelFolderCount += 1;
      } else if (entry.isDirectory) {
        topLevelFolderCount += 1;
      }

      traversePromises.push(
        this.traverseFileTree(
          entry,
          '',
          this.pendingFiles,
          this.pendingEmptyFolders
        )
      );
    }

    await Promise.all(traversePromises);

    // Force a new array reference so any display cache built from mid-traversal state is invalidated.
    this.pendingFiles = [...this.pendingFiles];
    this.pendingEmptyFolders = [...this.pendingEmptyFolders];

    if (this.isPackageLevel() && topLevelFolderCount > 1) {
      this.rejectMultipleFoldersSelection();
      return;
    }

    if (this.exceedsFileCountLimit(this.pendingFiles)) {
      this.rejectExcessiveFileCount();
      return;
    }

    // Check if individual files (not folders) are dropped at root level (not allowed)
    if (this.hasTopLevelFiles && this.isPackageLevel()) {
      this.messageService.alert(
        ErrorConstants.SELECT_OR_CREATE_FOLDER,
        this.appSettingService.toastrDisplayTime
      );
      this.pendingFiles = [];
      this.pendingEmptyFolders = [];
      return;
    }

    // Just store the files and folders, will process on OK click
    if (this.pendingFiles.length === 0 && this.pendingEmptyFolders.length === 0) {
      this.messageService.alert(
        ErrorConstants.FILE_FOLDER_MISSING_UPLOAD,
        this.appSettingService.toastrDisplayTime
      );
    }
  }

  private async validateAndCreatePackageFolder(packageFolderName: string): Promise<boolean> {
    try {
      // Validate package folder name according to bucket type
      if (this.currentBreadcrumbItem.BucketType === BucketTypeConstants.STUDYDATACOLLECTION) {
        if (!this.studyCodeHelperService.isValidStudyCode(packageFolderName)) {
          this.spinnerService.show(false);
          this.messageService.alert(
            ErrorConstants.INVALID_STUDY_CODE_MESSAGE,
            this.appSettingService.toastrDisplayTime
          );
          return false;
        }
      } else if (this.currentBreadcrumbItem.BucketType === BucketTypeConstants.FACILITYDATACOLLECTION) {
        if (this.studyCodeHelperService.isValidStudyCode(packageFolderName)) {
          this.spinnerService.show(false);
          this.messageService.alert(
            'Folder name cannot be a Study code format in Facility Data Collection bucket',
            this.appSettingService.toastrDisplayTime
          );
          return false;
        }
      }

      // Create the package folder
      const request: ICreateFolderRequest = {
        BucketId: this.currentBreadcrumbItem.BucketId,
        ParentId: this.currentBreadcrumbItem.ParentId,
        FolderName: packageFolderName,
        BucketType: this.currentBreadcrumbItem.BucketType,
        BUCode: this.currentBreadcrumbItem.BUCode
      };

      await this.bucketItemsService.CreateFolder(request).toPromise();

      // Fetch the created package folder to get its details
      const bucketItems: any = await this.bucketItemsService.GetBucketItems({
        BucketId: this.currentBreadcrumbItem.BucketId,
        BucketType: this.currentBreadcrumbItem.BucketType,
        BUCode: this.currentBreadcrumbItem.BUCode,
        DirectoryName: this.currentBreadcrumbItem.DirectoryName
      }).toPromise();

      const createdPackage = bucketItems.BucketItems.find((item: any) => item.LeafName === packageFolderName && item.Type === 'Folder');
      
      if (!createdPackage || !createdPackage.PackageId) {
        this.spinnerService.show(false);
        this.messageService.alert(
          'Failed to retrieve created package folder information',
          this.appSettingService.toastrDisplayTime
        );
        return false;
      }

      // Update current breadcrumb to point to the new package folder
      this.currentBreadcrumbItem = {
        ...this.currentBreadcrumbItem,
        PackageId: createdPackage.PackageId,
        ParentId: createdPackage.Id,
        DirectoryName: this.currentBreadcrumbItem.DirectoryName + '/' + packageFolderName
      };

      // Generate new GUID for this upload
      this.guid = this.utilityService.generateGuid();

      // Lock the package
      const lockSuccess = await this.bucketItemsService.LockPackage(createdPackage.PackageId, this.guid).toPromise();
      if (!lockSuccess) {
        this.spinnerService.show(false);
        this.messageService.alert(
          CommonConsants.PACKAGE_LOCK_ERROR,
          this.appSettingService.toastrDisplayTime
        );
        return false;
      }

      return true;
    } catch (error) {
      this.spinnerService.show(false);
      const errorMessage = error instanceof HttpErrorResponse && error.error?.detail
        ? error.error.detail
        : CommonConsants.FOLDER_CREATION_FAILED;
      // FOLDER_CREATION_FAILED/FOLDER_NAME_EXSISTS_ERROR are shown as-is, not wrapped as a validation error list
      const isKnownFolderError = errorMessage === CommonConsants.FOLDER_CREATION_FAILED
        || errorMessage === CommonConsants.FOLDER_NAME_EXSISTS_ERROR;
      this.messageService.alert(
        isKnownFolderError ? errorMessage : this.getUploadValidationErrorMessage([errorMessage]),
        this.appSettingService.toastrDisplayTime
      );
      return false;
    }
  }

  private async traverseFileTree(
    item: any,
    path: string,
    files: File[],
    emptyFolders: string[]
  ): Promise<void> {
    return new Promise((resolve) => {
      if (item.isFile) {
        item.file((file: File) => {
          // Add path information to the file
          Object.defineProperty(file, 'webkitRelativePath', {
            value: path + file.name,
            writable: true,
            configurable: true
          });
          files.push(file);
          resolve();
        });
      } else if (item.isDirectory) {
        const dirReader = item.createReader();
        this.readAllDirectoryEntries(dirReader).then(async (entries: any[]) => {
          if (entries.length === 0) {
            // This is an empty folder
            emptyFolders.push(path + item.name);
            resolve();
          } else {
            const subPath = path + item.name + '/';
            for (const entry of entries) {
              await this.traverseFileTree(entry, subPath, files, emptyFolders);
            }
            resolve();
          }
        });
      }
    });
  }

  private readAllDirectoryEntries(dirReader: any): Promise<any[]> {
    const readEntries = (): Promise<any[]> => {
      return new Promise((resolve) => {
        dirReader.readEntries(resolve);
      });
    };

    return new Promise(async (resolve) => {
      const entries: any[] = [];
      let batch = await readEntries();

      while (batch.length > 0) {
        entries.push(...batch);
        batch = await readEntries();
      }

      resolve(entries);
    });
  }

  private async createEmptyFolders(folderPaths: string[], shouldFinalize: boolean = true): Promise<boolean> {
    try {
      // Extract all unique folder paths including parent folders
      const allFolderPaths = new Set<string>();
      folderPaths.forEach(path => {
        const parts = path.split('/').filter(p => p.length > 0);
        // Add all ancestor paths
        for (let i = 1; i <= parts.length; i++) {
          const ancestorPath = parts.slice(0, i).join('/');
          allFolderPaths.add(ancestorPath);
        }
      });
 
      // Organize folders by hierarchy level
      const foldersByLevel: Map<number, string[]> = new Map();
      Array.from(allFolderPaths).forEach(path => {
        const parts = path.split('/').filter(p => p.length > 0);
        const level = parts.length;
        if (!foldersByLevel.has(level)) {
          foldersByLevel.set(level, []);
        }
        foldersByLevel.get(level).push(path);
      });

      // Sort levels to create parent folders first
      const sortedLevels = Array.from(foldersByLevel.keys()).sort((a, b) => a - b);

      // Track created folders and their IDs: Map<fullPath, folderId>
      const createdFolders: Map<string, string> = new Map();

      // Create folders level by level
      for (const level of sortedLevels) {
        const foldersAtLevel = foldersByLevel.get(level);
        
        for (const folderPath of foldersAtLevel) {
          const pathParts = folderPath.split('/').filter(p => p.length > 0);
          const folderName = pathParts[pathParts.length - 1];
          
          // Determine parent ID
          let parentId: string;
          let parentDirectoryName: string;
          
          if (pathParts.length === 1) {
            // Root level folder
            parentId = this.currentBreadcrumbItem.ParentId;
            parentDirectoryName = this.currentBreadcrumbItem.DirectoryName;
          } else {
            // Subfolder - get parent's ID from createdFolders
            const parentPath = pathParts.slice(0, -1).join('/');
            parentId = createdFolders.get(parentPath);
            
            if (!parentId) {
              throw new Error(`Parent folder not found for: ${folderPath}`);
            }
            
            // Construct parent directory name
            parentDirectoryName = this.currentBreadcrumbItem.DirectoryName + '/' + parentPath;
          }

          // Create the folder with only the folder name
          const request: ICreateFolderRequest = {
            BucketId: this.currentBreadcrumbItem.BucketId,
            ParentId: parentId,
            FolderName: folderName,
            BucketType: this.currentBreadcrumbItem.BucketType,
            BUCode: this.currentBreadcrumbItem.BUCode
          };

          await this.bucketItemsService.CreateFolder(request).toPromise();

          // Fetch the created folder to get its ID for use as parent
          const bucketItems: any = await this.bucketItemsService.GetBucketItems({
            BucketId: this.currentBreadcrumbItem.BucketId,
            BucketType: this.currentBreadcrumbItem.BucketType,
            BUCode: this.currentBreadcrumbItem.BUCode,
            DirectoryName: parentDirectoryName
          }).toPromise();

          if (bucketItems?.BucketItems && Array.isArray(bucketItems.BucketItems)) {
            const createdFolder = bucketItems.BucketItems.find((item: any) => item.LeafName === folderName && item.Type === 'Folder');
            if (createdFolder && createdFolder.Id) {
              createdFolders.set(folderPath, createdFolder.Id);
            }
          }
        }
      }
      
      // Only finalize if this is the final operation (no files to upload)
      if (shouldFinalize) {
        // Unlock package if it was locked
        if (this.guid && this.currentBreadcrumbItem.PackageId) {
          await this.bucketItemsService.UnlockPackage(this.currentBreadcrumbItem.PackageId, this.guid).toPromise();
          this.guid = '';
        }
        
        this.spinnerService.show(false);
        this.messageService.success(
          CommonConsants.FOLDER_CREATION_SUCCESS,
          this.appSettingService.toastrDisplayTime
        );
        this.dialogRef.close(true);
      }
      return true;
    } catch (error) {
      // Unlock package if it was locked
      if (this.guid && this.currentBreadcrumbItem.PackageId) {
        await this.bucketItemsService.UnlockPackage(this.currentBreadcrumbItem.PackageId, this.guid).toPromise();
        this.guid = '';
      }
      
      this.spinnerService.show(false);
      const errorMessage = error instanceof HttpErrorResponse && error.error?.detail
        ? error.error.detail
        : 'Failed to create empty folder(s)';
      // FOLDER_CREATION_FAILED/FOLDER_NAME_EXSISTS_ERROR/create-empty-folder failure are shown as-is, not wrapped as a validation error list
      const isKnownFolderError = errorMessage === CommonConsants.FOLDER_CREATION_FAILED
        || errorMessage === CommonConsants.FOLDER_NAME_EXSISTS_ERROR
        || errorMessage === 'Failed to create empty folder(s)';
      this.messageService.alert(
        isKnownFolderError ? errorMessage : this.getUploadValidationErrorMessage([errorMessage]),
        this.appSettingService.toastrDisplayTime
      );
      return false;
    }
  }

  // Handles file upload and shows a custom success message
  private handleFiles(fileList: FileList | File[], successMessage: string): void {
    this.spinnerService.show(true);
    const files: File[] = Array.from(fileList);
    this.completedRequestedCount = [];

    if (this.validationsForUpload(files)) {
      this.createFileList(files).then((fileInfoList) => {
        this.uploadFileAndFolder(fileInfoList, successMessage);
      }).catch(() => {
        this.spinnerService.show(false);
        if (this.guid && this.currentBreadcrumbItem?.PackageId) {
          this.bucketItemsService.UnlockPackage(this.currentBreadcrumbItem.PackageId, this.guid).subscribe(
            {
              next: () => {
                this.guid = '';
              }
            }
          );
        }
      });
    } else {
      if (this.guid && this.currentBreadcrumbItem?.PackageId) {
        this.bucketItemsService.UnlockPackage(this.currentBreadcrumbItem.PackageId, this.guid).subscribe(
          {
            next: () => {
              this.guid = '';
            }
          }
        );
      }
    }
    if (files.length === 0) {
      this.spinnerService.show(false);
    }
  }

  private async createFileList(files: File[]): Promise<FormData[]> {
    let fileInfoList: FormData[] = [];
    let totalSizePerRequestCheck: number = 0;
    let formData = new FormData();
    var checksum: string = "";
    
    // Only lock package if not already locked (guid not set)
    if (!this.guid) {
      this.guid = this.utilityService.generateGuid();

      try {
        await new Promise<boolean>((resolve, reject) => {
          this.bucketItemsService.LockPackage(this.currentBreadcrumbItem.PackageId, this.guid).subscribe({
            next: (value) => {
              if (value) {
                resolve(true);
              } else {
                reject(new Error("Locking failed"));
              }
            },
            error: (error) => {
              reject(error);
            }
          });
        });
      } catch (error) {
        this.spinnerService.show(false);
        this.messageService.alert(
          CommonConsants.PACKAGE_LOCK_ERROR,
          this.appSettingService.toastrDisplayTime
        );
        return Promise.reject(error);
      }
    }

    for (let i = 0; i < files.length; i++) {
      await this.checksumService
        .CalculateSHA512(files[i])
        .then((x) => {
          checksum = x;
        })
        .catch((error) => {
          this.spinnerService.show(false);
          this.messageService.alert(
            ErrorConstants.CHECKSUM_FAIL,
            this.appSettingService.toastrDisplayTime
          );
          return throwError(() => new Error(error));
        });
      if (files[i].size / 1048576 > 25) {
        let fileFormData = new FormData();
        let fileName =
          files[i].webkitRelativePath && files[i].webkitRelativePath !== ""
            ? files[i].webkitRelativePath
            : files[i].name;
        
        const fileLastModifiedOn = new Date(files[i].lastModified).toUTCString();
        fileFormData.append("Files", files[i], `${fileName}::${checksum}::${fileLastModifiedOn}`);
        fileInfoList.push(this.createMetadata(fileFormData));
      } else {
        totalSizePerRequestCheck =
          totalSizePerRequestCheck + files[i].size / 1048576;
        if (totalSizePerRequestCheck <= 25) {
          let fileName =
            files[i].webkitRelativePath && files[i].webkitRelativePath !== ""
              ? files[i].webkitRelativePath
              : files[i].name;
          const fileLastModifiedOn = new Date(files[i].lastModified).toUTCString();
          formData.append("Files", files[i], `${fileName}::${checksum}::${fileLastModifiedOn}`);
        } else {
          fileInfoList.push(this.createMetadata(formData));
          i -= 1;
          totalSizePerRequestCheck = 0;
          formData = new FormData();
        }
      }
    }
    if (formData.has("Files")) {
      fileInfoList.push(this.createMetadata(formData));
    }
    return fileInfoList;
  }

  private createMetadata(formData: FormData): FormData {
    formData.append("TransactionId", this.guid);
    formData.append("BucketId", this.currentBreadcrumbItem.BucketId);
    formData.append("BucketType", this.currentBreadcrumbItem.BucketType);
    formData.append("BUCode", this.currentBreadcrumbItem.BUCode);
    formData.append("DirName", this.currentBreadcrumbItem.DirectoryName);
    formData.append("ParentId", this.currentBreadcrumbItem.ParentId);
    return formData;
  }

  private uploadFileAndFolder(fileInfoList: FormData[], successMessage: string): void {
    this.hasValidationErrorsInUploadResponse = false;
    this.validationErrorToasterShown = false;
    this.commitRequest = {
      TransactionId: this.guid,
      BucketId: this.currentBreadcrumbItem.BucketId,
      BucketType: this.currentBreadcrumbItem.BucketType,
      BUCode: this.currentBreadcrumbItem.BUCode,
      DirName: this.currentBreadcrumbItem.DirectoryName,
    };
    from(fileInfoList)
      .pipe(
        mergeMap((i) => this.bucketItemsService.UploadItem(i),4),
        takeUntil(this.cancel$),
        catchError((error: HttpErrorResponse) => {
          return this.handleError(error);
        })
      )
      .subscribe((response) => {
        const validationErrors = this.getUploadValidationErrors(response);
        if (validationErrors && validationErrors.length > 0) {
          this.hasValidationErrorsInUploadResponse = true;
          this.spinnerService.show(false);
          this.bucketItemsService.UnlockPackage(this.currentBreadcrumbItem.PackageId, this.guid).subscribe(
            {
              next: () => {
                this.guid = '';
              }
            }
          );

          if (!this.validationErrorToasterShown) {
            this.validationErrorToasterShown = true;
            this.messageService.alert(
              this.getUploadValidationErrorMessage(validationErrors),
              this.appSettingService.toastrDisplayTime
            );
          }
          return;
        }

        if (this.hasValidationErrorsInUploadResponse) {
          return;
        }

        this.completedRequestedCount.push(true);
        if (fileInfoList.length === this.completedRequestedCount.length) {
          this.bucketItemsService
            .CreateFoldersForFile(this.commitRequest)
            .subscribe({
              next: (res: any) => {
                if (res) {
                  if (res.JustificationRequired) {
                    this.spinnerService.show(false);
                    localStorage.setItem("AutomatedTransfersScreenJustificationPopup", "true");
                    this.dialog.open(JustificationDialogComponent, {
                      width: "450px",
                      disableClose: true,
                    }).afterClosed().subscribe((response: { comment: string }) => {
                      if (response && response.comment) {
                        this.commitRequest.Comment = response.comment;
                        this.continueAfterJustificationWithSuccess(successMessage);
                      } else {
                        if (this.inputElement) {
                          this.inputElement.value = null;
                        }
                      }
                      localStorage.setItem("AutomatedTransfersScreenJustificationPopup", "false");
                    });
                    return;
                  }
                  this.bucketItemsService
                    .Commit(this.commitRequest)
                    .pipe(
                      map((res: string[]) => {
                        this.dialogRef.close(res);
                        this.spinnerService.show(false);
                        this.handleCommitResult(res, successMessage);
                      }),
                      catchError((error: HttpErrorResponse) => {
                        return this.handleError(error);
                      })
                    )
                    .subscribe();
                }
              },
              error: (error: HttpErrorResponse) => {
                this.handleError(error);
              },
            });
        }
      });
  }

 private handleCommitResult(res: string[], successMessage: string): void {
    if (res.length === 0) {
      this.messageService.success(
        successMessage,
        this.appSettingService.toastrDisplayTime
      );
      return;
    }
    if (this.commitRequest.Comment) {
      this.dialog.open(DuplicateFileUploadDialogComponent, {
        width: "529px",
        disableClose: true,
        data: [...res],
      });
      return;
    }
    this.dialog.open(JustificationDialogComponent, {
      width: "450px",
      disableClose: true,
    }).afterClosed().subscribe((response: { comment: string }) => {
      if (response && response.comment) {
        this.dialog.open(DuplicateFileUploadDialogComponent, {
          width: "529px",
          disableClose: true,
          data: [...res],
        });
      }
    });
  }

  private getUploadValidationErrors(response: unknown): string[] | null {
    if (!Array.isArray(response)) {
      return null;
    }
    return response;
  }

  private getUploadValidationErrorMessage(errors: string[]): string {
    return `Upload failed due to following reason(s)\n\n${errors.map((error) => `- ${error}`).join("\n")}`;
  }


  private getTotalSelectedSizeBytes(): number {
    return this.pendingFiles.reduce((sum, file) => sum + file.size, 0);
  }

  exceedsMaxTotalSize(): boolean {
    return this.getTotalSelectedSizeBytes() > this.maxUploadFileSizeBytes;
  }

  private hasMultipleTopLevelFolders(files: File[]): boolean {
    const topLevelFolders = new Set<string>();

    files.forEach((file) => {
      const relativePath = file.webkitRelativePath || file.name;
      const parts = relativePath.split("/").filter((part) => part.length > 0);

      if (parts.length > 1) {
        topLevelFolders.add(parts[0]);
      }
    });

    return topLevelFolders.size > 1;
  }

  private clearPendingSelection(): void {
    this.pendingFiles = [];
    this.pendingEmptyFolders = [];
    this.pendingPackageFolderName = null;
    this.hasTopLevelFiles = false;
  }

  private rejectMultipleFoldersSelection(): void {
    this.clearPendingSelection();
    this.messageService.alert(
      ErrorConstants.ONLY_ONE_FOLDER_ALLOWED_AT_PACKAGE_LEVEL,
      this.appSettingService.toastrDisplayTime
    );
  }

  private exceedsFileCountLimit(files: File[]): boolean {
    return !!files && files.length > this.maxUploadFileCount;
  }

  private rejectExcessiveFileCount(): void {
    this.clearPendingSelection();
    this.messageService.alert(
      this.getUploadValidationErrorMessage([ErrorConstants.FILE_NUMBER_LIMIT]),
      this.appSettingService.toastrDisplayTime
    );
  }

  // Used for justification flow with custom success message
  private continueAfterJustificationWithSuccess(successMessage: string): void {
    // Keep the spinner up while CreateFoldersForFile/Commit run, so drag-and-drop stays non-interactive until the result dialog opens
    this.spinnerService.show(true);
    this.bucketItemsService.CreateFoldersForFile(this.commitRequest).subscribe({
      next: (response: any) => {
        if (response) {
          this.bucketItemsService
            .Commit(this.commitRequest)
            .pipe(
              map((res: string[]) => {
                this.dialogRef.close(res);
                this.spinnerService.show(false);
                this.handleCommitResult(res, successMessage);
              }),
              catchError((error: HttpErrorResponse) => {
                return this.handleError(error);
              })
            )
            .subscribe();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.handleError(error);
      },
    });
  }

  private handleError(error: HttpErrorResponse) {
    this.spinnerService.show(false);
    this.bucketItemsService.UnlockPackage(this.currentBreadcrumbItem.PackageId, this.guid).subscribe(
      {
        next: () => {
          this.guid = '';
        }
      }
    );
    if (error.error.detail != undefined) {
      this.messageService.alert(
        error.error.detail,
        this.appSettingService.toastrDisplayTime
      );
    } else {
      this.messageService.alert(
        ErrorConstants.UPLOAD_FAIL,
        this.appSettingService.toastrDisplayTime
      );
    }
    return throwError(() => new Error(error.message));
  }

  private validationsForUpload(files: File[]): boolean {
    if (!files) {
      this.spinnerService.show(false);
      this.messageService.alert(
        this.getUploadValidationErrorMessage([ErrorConstants.FILE_FOLDER_MISSING_UPLOAD]),
        this.appSettingService.toastrDisplayTime
      );
      return false;
    } else if (this.exceedsFileCountLimit(files)) {
      this.spinnerService.show(false);
      this.messageService.alert(
        this.getUploadValidationErrorMessage([ErrorConstants.FILE_NUMBER_LIMIT]),
        this.appSettingService.toastrDisplayTime
      );
      return false;
    }
    return true;
  }

  async onOkClick(): Promise<void> {
    // Process pending files and folders
    if (this.pendingFiles.length === 0 && this.pendingEmptyFolders.length === 0) {
      this.dialogRef.close(false);
      return;
    }

    // If dropping folder at bucket level, create package folder first
    if (this.pendingPackageFolderName && this.isPackageLevel()) {
      if (!this.packageFolderCreated) {
        this.packageLevelSnapshot = true;
        this.spinnerService.show(true);

        // Validate package folder name according to bucket type
        const isValidPackageFolder = await this.validateAndCreatePackageFolder(this.pendingPackageFolderName);
        if (!isValidPackageFolder) {
          return; // Validation failed, error already shown
        }

        this.packageFolderCreated = true;
      }

      // Strip package folder prefix from all paths
      const strippedFiles = this.pendingFiles.map((file) => {
        const relativePath = file.webkitRelativePath || file.name;
        if (!relativePath.startsWith(this.pendingPackageFolderName + '/')) {
          return file;
        }

        const newPath = relativePath.substring(this.pendingPackageFolderName.length + 1);
        const strippedFile = new File([file], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });

        Object.defineProperty(strippedFile, 'webkitRelativePath', {
          value: newPath,
          writable: true,
          configurable: true
        });

        return strippedFile;
      });

      const strippedEmptyFolders = this.pendingEmptyFolders
        .map(folder => {
          if (folder.startsWith(this.pendingPackageFolderName + '/')) {
            return folder.substring(this.pendingPackageFolderName.length + 1);
          }
          return folder;
        })
        .filter(folder => folder !== this.pendingPackageFolderName && folder.length > 0);

      // Handle empty folders first
      if (strippedEmptyFolders.length > 0) {
        const shouldFinalize = strippedFiles.length === 0;
        const foldersCreated = await this.createEmptyFolders(strippedEmptyFolders, shouldFinalize);
        if (!foldersCreated) {
          return;
        }
      }

      // Then handle files if any
      if (strippedFiles.length > 0) {
        this.handleFiles(strippedFiles as any, CommonConsants.FOLDER_CREATION_SUCCESS);
      } else if (strippedEmptyFolders.length === 0) {
        // Empty package folder was created - unlock it before closing
        if (this.guid && this.currentBreadcrumbItem.PackageId) {
          await this.bucketItemsService.UnlockPackage(this.currentBreadcrumbItem.PackageId, this.guid).toPromise();
          this.guid = '';
        }
        this.spinnerService.show(false);
        this.messageService.success(
          CommonConsants.FOLDER_CREATION_SUCCESS,
          this.appSettingService.toastrDisplayTime
        );
        this.dialogRef.close(true);
      }
    } else {
      // Normal flow - not creating package folder
      // Handle empty folders first
      if (this.pendingEmptyFolders.length > 0) {
        this.spinnerService.show(true);
        const shouldFinalize = this.pendingFiles.length === 0;
        const foldersCreated = await this.createEmptyFolders(this.pendingEmptyFolders, shouldFinalize);
        if (!foldersCreated) {
          return;
        }
      }

      // Then handle files if any
      if (this.pendingFiles.length > 0) {
        this.handleFiles(this.pendingFiles as any, CommonConsants.SUCCESS_FILE_UPLOAD);
      }
    }
  }

  close(): void {
    // Clear pending files and folders on cancel
    this.clearPendingSelection();
    this.packageLevelSnapshot = null;
    this.packageFolderCreated = false;
    this.dialogRef.close(false);
  }

  async cancelUpload(): Promise<void> {
    this.cancel$.next();
    this.spinnerService.show(false);
    const guidToUnlock = this.guid;
    this.guid = '';
    this.completedRequestedCount = [];
    if (guidToUnlock && this.currentBreadcrumbItem?.PackageId) {
      try {
        await this.bucketItemsService
          .UnlockPackage(this.currentBreadcrumbItem.PackageId, guidToUnlock)
          .toPromise();
        this.guid = '';
      } catch {
        // Ignore unlock failures and continue closing dialog
      }
    }
    this.packageLevelSnapshot = null;
    this.packageFolderCreated = false;
    this.dialogRef.close(false);
  }

  getTotalItemsCount(): number {
    return this.getDisplayItems().length;
  }

  getTotalSelectedSizeLabel(): string {
    const totalBytes = this.getTotalSelectedSizeBytes();
    if (totalBytes <= 0) {
      return '';
    }

    return this.formatBytesBreakdown(totalBytes);
  }

  getDisplayItems(): SelectionDisplayItem[] {
    if (
      this.displayItemsCache &&
      this.displayItemsCacheFiles === this.pendingFiles &&
      this.displayItemsCacheFolders === this.pendingEmptyFolders
    ) {
      return this.displayItemsCache;
    }

    const rootNodes = this.buildSelectionTree();
    const displayItems: SelectionDisplayItem[] = [];

    this.flattenSelectionTree(Array.from(rootNodes.values()), 0, displayItems);

    this.displayItemsCache = displayItems;
    this.displayItemsCacheFiles = this.pendingFiles;
    this.displayItemsCacheFolders = this.pendingEmptyFolders;

    return displayItems;
  }

  getItemIcon(itemName: string): string {
    // Check if it's likely a file (has extension) or folder
    const hasExtension = itemName.includes('.');
    const file = this.pendingFiles.find(f => f.name === itemName);
    
    if (file || hasExtension) {
      return 'fa fa-file-o';
    }
    return 'fa fa-folder-o';
  }

  removeSelectedItem(item: SelectionDisplayItem): void {
    if (this.isLoading) {
      return;
    }

    if (!item) {
      return;
    }

    const selectedPath = item.path;
    if (!selectedPath) {
      return;
    }

    if (item.type === 'file') {
      this.pendingFiles = this.pendingFiles.filter((file) => (file.webkitRelativePath || file.name) !== selectedPath);

      // Keep the parent folder visible as an empty folder instead of letting it disappear
      const separatorIndex = selectedPath.lastIndexOf('/');
      if (separatorIndex > 0) {
        const parentFolderPath = selectedPath.substring(0, separatorIndex);
        const parentFolderPrefix = `${parentFolderPath}/`;
        const hasRemainingFiles = this.pendingFiles.some((file) => {
          const path = file.webkitRelativePath || file.name;
          return path === parentFolderPath || path.startsWith(parentFolderPrefix);
        });

        if (!hasRemainingFiles && !this.pendingEmptyFolders.includes(parentFolderPath)) {
          this.pendingEmptyFolders = [...this.pendingEmptyFolders, parentFolderPath];
        }
      }
      return;
    }

    const folderPrefix = `${selectedPath}/`;
    this.pendingFiles = this.pendingFiles.filter((file) => {
      const path = file.webkitRelativePath || file.name;
      return path !== selectedPath && !path.startsWith(folderPrefix);
    });

    this.pendingEmptyFolders = this.pendingEmptyFolders.filter((folder) => {
      return folder !== selectedPath && !folder.startsWith(folderPrefix);
    });
  }

  isPackageLevel(): boolean {
    return this.packageLevelSnapshot ?? this.currentBreadcrumbItem?.DirectoryName.split("/").length === 2;
  }

  isOkDisabled(): boolean {
    if (this.isLoading) {
      return true;
    }

    if (this.exceedsMaxTotalSize()) {
      return true;
    }

    if (!this.isPackageLevel()) {
      return false;
    }

    return this.pendingFiles.length === 0 && this.pendingEmptyFolders.length === 0;
  }

  getDisplayFiles(): string[] {
    return this.pendingFiles.map(file => {
      const path = file.webkitRelativePath || file.name;
      // Extract just the file name (last part of the path)
      const parts = path.split('/');
      return parts[parts.length - 1];
    });
  }

  getDisplayFolders(): string[] {
    // Get only top-level folder names
    const topLevelFolders = new Set<string>();
    this.pendingEmptyFolders.forEach(folder => {
      const parts = folder.split('/').filter(p => p.length > 0);
      if (parts.length > 0) {
        topLevelFolders.add(parts[0]);
      }
    });
    return Array.from(topLevelFolders);
  }

  private buildSelectionTree(): Map<string, SelectionTreeNode> {
    const rootNodes = new Map<string, SelectionTreeNode>();

    this.pendingFiles.forEach((file) => {
      const path = file.webkitRelativePath || file.name;
      const parts = path.split('/').filter((part) => part.length > 0);

      if (parts.length === 0) {
        return;
      }

      this.addFileToTree(rootNodes, parts, file.size);
    });

    this.pendingEmptyFolders.forEach((folder) => {
      const parts = folder.split('/').filter((part) => part.length > 0);

      if (parts.length === 0) {
        return;
      }

      this.addFolderToTree(rootNodes, parts);
    });

    return rootNodes;
  }

  private addFileToTree(rootNodes: Map<string, SelectionTreeNode>, parts: string[], size: number): void {
    let currentNodes = rootNodes;
    const ancestorNodes: SelectionTreeNode[] = [];
    const folderParts = parts.slice(0, -1);

    folderParts.forEach((part, index) => {
      const path = parts.slice(0, index + 1).join('/');
      let folderNode = currentNodes.get(part);

      if (!folderNode) {
        folderNode = {
          key: `folder:${path}`,
          path,
          name: part,
          type: 'folder',
          size: 0,
          children: new Map<string, SelectionTreeNode>(),
        };
        currentNodes.set(part, folderNode);
      }

      ancestorNodes.push(folderNode);
      currentNodes = folderNode.children;
    });

    const fileName = parts[parts.length - 1];
    const filePath = parts.join('/');
    if (!currentNodes.has(fileName)) {
      currentNodes.set(fileName, {
        key: `file:${filePath}`,
        path: filePath,
        name: fileName,
        type: 'file',
        size,
        children: new Map<string, SelectionTreeNode>(),
      });
    }

    ancestorNodes.forEach((node) => {
      node.size += size;
    });
  }

  private addFolderToTree(rootNodes: Map<string, SelectionTreeNode>, parts: string[]): void {
    let currentNodes = rootNodes;

    parts.forEach((part, index) => {
      const path = parts.slice(0, index + 1).join('/');
      let folderNode = currentNodes.get(part);

      if (!folderNode) {
        folderNode = {
          key: `folder:${path}`,
          path,
          name: part,
          type: 'folder',
          size: 0,
          children: new Map<string, SelectionTreeNode>(),
        };
        currentNodes.set(part, folderNode);
      }

      currentNodes = folderNode.children;
    });
  }

  private flattenSelectionTree(nodes: SelectionTreeNode[], depth: number, displayItems: SelectionDisplayItem[]): void {
    const folderNodes = nodes.filter((node) => node.type === 'folder');
    const fileNodes = nodes.filter((node) => node.type === 'file');
    const orderedNodes = [...folderNodes, ...fileNodes];

    orderedNodes.forEach((node) => {
      displayItems.push({
        key: node.key,
        path: node.path,
        name: node.name,
        type: node.type,
        size: node.size,
        sizeLabel: this.formatBytes(node.size),
        depth,
      });

      if (node.type === 'folder' && node.children.size > 0) {
        this.flattenSelectionTree(Array.from(node.children.values()), depth + 1, displayItems);
      }
    });
  }

  private formatBytes(bytes: number): string {
    if (!+bytes) {
      return '0 Bytes';
    }

    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const base = 1024;
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(base)), sizes.length - 1);
    return `${parseFloat((bytes / Math.pow(base, unitIndex)).toFixed(2))} ${sizes[unitIndex]}`;
  }

  // Breaks total size into GB/MB/KB/Bytes components, e.g. "2 GB 5 Bytes", omitting zero-value units.
  private formatBytesBreakdown(bytes: number): string {
    if (!+bytes) {
      return '0 Bytes';
    }

    const units: { label: string; bytes: number }[] = [
      { label: 'GB', bytes: 1024 ** 3 },
      { label: 'MB', bytes: 1024 ** 2 },
      { label: 'KB', bytes: 1024 },
      { label: 'Bytes', bytes: 1 },
    ];

    let remaining = Math.floor(bytes);
    const parts: string[] = [];

    units.forEach((unit) => {
      const value = Math.floor(remaining / unit.bytes);
      if (value > 0) {
        parts.push(`${value} ${unit.label}`);
        remaining -= value * unit.bytes;
      }
    });

    return parts.length > 0 ? parts.join(' ') : '0 Bytes';
  }
}

